import React, { useState } from 'react';
import { Phone, ArrowLeft, Calendar, User, MessageSquare, CheckCircle, Smartphone, BatteryCharging, Layers, Plug, Camera, Volume2, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useSEO } from '../hooks/useSEO';
import { ENTREPRISE, HORAIRES_COURT, RDV_MENTION } from '../data/entreprise';

const PANNES = [
  { value: "Remplacement d'écran", label: 'Écran cassé', icon: Smartphone },
  { value: 'Changement de batterie', label: 'Batterie', icon: BatteryCharging },
  { value: 'Réparation connecteur de charge', label: 'Ne charge plus', icon: Plug },
  { value: 'Vitre arrière', label: 'Vitre arrière', icon: Layers },
  { value: 'Problème de caméra', label: 'Caméra', icon: Camera },
  { value: 'Problème de haut-parleur', label: 'Son / micro', icon: Volume2 },
  { value: 'Autre', label: 'Autre panne', icon: HelpCircle },
];

const EMPTY = { prenom: '', nom: '', numero: '', typeReparation: '', message: '' };

const RendezVousPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  useSEO({
    title: 'Prendre rendez-vous – Réparation iPhone & Samsung Saint-Chamond | Suro Phone',
    description: 'Demandez votre rendez-vous en 30 secondes : écran, batterie, connecteur. Devis gratuit, réparation le jour même à Saint-Chamond ou à domicile. ☎ 07.73.60.15.21',
    path: '/rendez-vous',
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.typeReparation) {
      toast({ title: 'Choisissez votre panne', description: 'Sélectionnez le type de réparation ci-dessus.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rendez-vous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error("Erreur lors de l'envoi");
      setSent(true);
      setFormData(EMPTY);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue. Veuillez réessayer ou nous appeler directement.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="rdv-page">
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} data-testid="rdv-back-btn" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          <a href="tel:0773601521" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <Phone className="w-4 h-4" />
            <span className="font-medium">07.73.60.15.21</span>
          </a>
        </div>
      </nav>

      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-10 md:py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <Calendar className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3" />
          <h1 className="text-3xl md:text-5xl font-bold mb-3">Prendre rendez-vous</h1>
          <p className="text-base md:text-xl text-blue-100">30 secondes suffisent : on vous rappelle rapidement avec un devis gratuit.</p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
        {sent ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="rdv-success">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Demande envoyée !</h2>
            <p className="text-gray-600 mb-6">Merci, nous vous rappelons très vite pour confirmer le rendez-vous et vous donner le tarif.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:0773601521" className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-xl font-bold">
                <Phone className="w-5 h-5" /> Appeler maintenant
              </a>
              <Button variant="outline" onClick={() => navigate('/')} data-testid="rdv-home-btn" className="px-6 py-4 rounded-xl font-bold">
                Retour à l'accueil
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-5 md:p-8 space-y-7" data-testid="rdv-form">
            <div>
              <p className="font-bold text-gray-900 mb-3 text-lg">1. Quelle est la panne ? *</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="radiogroup">
                {PANNES.map(({ value, label, icon: Icon }) => {
                  const active = formData.typeReparation === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`panne-${value.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                      onClick={() => setFormData({ ...formData, typeReparation: value })}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-sm font-semibold transition-colors ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'}`}
                    >
                      <Icon className="w-7 h-7" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="font-bold text-gray-900 mb-3 text-lg">2. Vos coordonnées *</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="prenom" className="flex items-center gap-2 mb-2"><User className="w-4 h-4" /> Prénom</Label>
                  <Input id="prenom" name="prenom" type="text" placeholder="Votre prénom" value={formData.prenom} onChange={handleChange} required autoComplete="given-name" data-testid="rdv-prenom-input" />
                </div>
                <div>
                  <Label htmlFor="nom" className="flex items-center gap-2 mb-2"><User className="w-4 h-4" /> Nom <span className="text-gray-400 font-normal">(facultatif)</span></Label>
                  <Input id="nom" name="nom" type="text" placeholder="Votre nom" value={formData.nom} onChange={handleChange} autoComplete="family-name" data-testid="rdv-nom-input" />
                </div>
                <div>
                  <Label htmlFor="numero" className="flex items-center gap-2 mb-2"><Phone className="w-4 h-4" /> Téléphone</Label>
                  <Input id="numero" name="numero" type="tel" inputMode="tel" placeholder="06 XX XX XX XX" value={formData.numero} onChange={handleChange} required autoComplete="tel" data-testid="rdv-numero-input" />
                </div>
              </div>
            </div>

            <div>
              <p className="font-bold text-gray-900 mb-3 text-lg">3. Votre téléphone <span className="text-gray-400 font-normal text-base">(facultatif)</span></p>
              <Label htmlFor="message" className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4" /> Modèle et détails</Label>
              <Textarea id="message" name="message" placeholder="Ex : iPhone 13, écran fissuré, tactile OK. Je préfère à domicile à Rive-de-Gier." value={formData.message} onChange={handleChange} rows={3} data-testid="rdv-message-input" />
            </div>

            <Button type="submit" disabled={isSubmitting} data-testid="rdv-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-bold rounded-xl">
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
            <p className="text-sm text-gray-500 text-center">Devis gratuit • Sans engagement • Réponse rapide</p>
            <p className="text-sm text-gray-500 text-center" data-testid="rdv-horaires">{RDV_MENTION} · {HORAIRES_COURT}<br />{ENTREPRISE.adresse}</p>
          </form>
        )}

        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">Plus rapide ? Appelez ou envoyez une photo de la panne sur WhatsApp</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:0773601521" data-testid="rdv-call-btn" className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-colors">
              <Phone className="w-5 h-5" /> 07.73.60.15.21
            </a>
            <a href="https://wa.me/33773601521?text=Bonjour%2C%20je%20souhaite%20prendre%20rendez-vous%20pour%20r%C3%A9parer%20mon%20t%C3%A9l%C3%A9phone." target="_blank" rel="noopener noreferrer" data-testid="rdv-whatsapp-btn" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white px-8 py-4 rounded-xl font-bold text-lg transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RendezVousPage;
